import { Link } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const StyledLayoutBaseWrapper = styled.div`
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background.default};
  display: flex;
  &::after {
    content: '';
    min-height: inherit;
    font-size: 0;
  }
`;

const SIDEBAR_COLLAPSED_WIDTH = '4.5rem';

export const StyledLayoutBaseSidebar = styled.div<{ collapsed?: boolean }>`
  width: ${({ theme, collapsed }) =>
    collapsed ? SIDEBAR_COLLAPSED_WIDTH : theme.dimensions.sidebar.width};
  background-color: ${({ theme }) => theme.colors.background.light};
  flex-shrink: 0;
  padding: ${({ collapsed }) =>
    collapsed ? '1.25rem 0.5rem 1rem' : '1.5rem 1rem 1.25rem'};
  border-right: 1px solid ${({ theme }) => theme.colors.background.medium};
  display: flex;
  flex-direction: column;
  position: relative;
  /* Must beat the page content card's stacking context (z-index: auto)
     so the workspace dropdown can overlay it, but stay BELOW the modal
     z-index (theme.zindexes.modal === 15) so customer/order detail
     modals can cover the sidebar. */
  z-index: 1;
  transition: width 0.22s ease, padding 0.22s ease;
`;

export const StyledLayoutBaseDropdown = styled.div<{ collapsed?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
  ${({ collapsed }) =>
    collapsed &&
    css`
      display: none;
    `}
`;

export const StyledSidebarIdentity = styled(Link)<{ collapsed?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: ${({ collapsed }) =>
    collapsed ? '0.5rem 0' : '0.625rem 0.75rem'};
  margin-bottom: 0.875rem;
  border-radius: 0.75rem;
  text-decoration: none;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.brand.navy} 0%,
    ${({ theme }) => theme.colors.brand.navy} 60%,
    #1a2255 100%
  );
  color: #ffffff;
  transition: ${({ theme }) => theme.transitions.default};
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(9, 15, 56, 0.16);
  ${({ collapsed }) =>
    collapsed &&
    css`
      justify-content: center;
    `}

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 88% 50%,
      ${({ theme }) => theme.colors.brand.yellow}24,
      transparent 60%
    );
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px -4px rgba(9, 15, 56, 0.24);
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

export const StyledSidebarIdentityName = styled.span`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

export const StyledCollapseToggle = styled.button<{ collapsed?: boolean }>`
  position: absolute;
  top: 2rem;
  ${({ theme }) => (theme.direction === 'rtl' ? 'left: -0.75rem;' : 'right: -0.75rem;')}
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.background.medium};
  background: ${({ theme }) => theme.colors.background.light};
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  box-shadow: ${({ theme }) => theme.shadows.small};
  transition: ${({ theme }) => theme.transitions.default};

  svg {
    width: 0.9rem;
    height: 0.9rem;
    transition: transform 0.22s ease;
    transform: ${({ collapsed, theme }) => {
      const isRtl = theme.direction === 'rtl';
      if (isRtl) return collapsed ? 'rotate(180deg)' : 'rotate(0deg)';
      return collapsed ? 'rotate(180deg)' : 'rotate(0deg)';
    }};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.background.default};
    color: ${({ theme }) => theme.colors.accent};
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

export const StyledSidebarSpacer = styled.div`
  flex: 1;
`;

export const StyledSidebarFooter = styled.div<{ collapsed?: boolean }>`
  display: flex;
  flex-direction: ${({ collapsed }) => (collapsed ? 'column' : 'row')};
  align-items: center;
  gap: ${({ collapsed }) => (collapsed ? '0.375rem' : '0.5rem')};
  padding: ${({ collapsed }) =>
    collapsed ? '0.5rem 0' : '0.5rem 0.625rem'};
  border-radius: 0.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.background.medium};
  margin-top: 0.5rem;
  padding-top: 0.875rem;
`;

export const StyledPoweredByLabel = styled.span`
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${({ theme }) => theme.colors.brand.navy};
  white-space: nowrap;
  position: relative;
  padding-left: 0.5rem;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 0.625rem;
    background: ${({ theme }) => theme.colors.brand.yellow};
    border-radius: 2px;
  }
`;

export const StyledFooterLogoMini = styled.div<{ collapsed?: boolean }>`
  display: flex;
  align-items: center;
  opacity: 0.85;
  svg {
    font-size: ${({ collapsed }) => (collapsed ? '1.6rem' : '1.3rem')} !important;
    height: 1em !important;
    width: 0.7em !important;
  }
`;

export const StyledLayoutBaseMain = styled.main`
  width: calc(100% - ${({ theme }) => theme.dimensions.sidebar.width});
  flex: 1;
  max-height: 100vh;
  overflow: auto;
  padding: 1.5rem 1.75rem;
  transition: width 0.22s ease;
`;

export const StyledLayoutBaseContent = styled.div`
  background-color: ${({ theme }) => theme.colors.background.light};
  border-radius: 0.875rem;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04),
    0 4px 12px -4px rgba(16, 24, 40, 0.06);
  padding: 1.75rem 2rem;
  min-height: calc(100vh - 3rem);
  animation: ${fadeUp} 0.32s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.colors.brand.green}66,
      ${({ theme }) => theme.colors.brand.yellow}66,
      ${({ theme }) => theme.colors.brand.navy}33,
      transparent
    );
    pointer-events: none;
  }
`;

export const StyledSwitcherBox = styled.div`
  display: grid;
  grid-template-columns: repeat(2, auto);
  grid-column-gap: 0.75rem;
`;
